import { useState } from 'react';
import { format } from 'date-fns';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, downloadFile } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Download, FileText, Loader2 } from 'lucide-react';
import { useMembership } from '@/hooks/useMembership';
import { usePonds } from '@/hooks/usePonds';

interface Cycle {
  id: string;
  cycle_number: number | null;
  stocking_date: string | null; // null while preparing (#191)
  status: string;
  stocking_density: number | null;
}

interface CyclesResponse {
  data: Cycle[];
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'not stocked yet';
  return new Date(dateStr).toLocaleDateString('en-PH', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function reportFileName(pondName: string | undefined, cycleNumber: number | null | undefined): string {
  const pond = (pondName ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'pond';
  return `${pond}-crop${cycleNumber ?? ''}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
}

export function CycleReportCard() {
  const { membership } = useMembership();
  const farmId = membership?.farmId;

  const [selectedPond, setSelectedPond] = useState('');
  const [selectedCycle, setSelectedCycle] = useState('');

  // Get ponds for selection
  const { data: ponds = [], isLoading: pondsLoading } = usePonds(farmId);

  // Get cycles for selected pond
  const { data: cyclesData, isLoading: cyclesLoading, isError: cyclesError } = useQuery({
    queryKey: ['cycles', farmId, selectedPond],
    queryFn: () => api.get<CyclesResponse>(`/api/v1/farms/${farmId}/cycles`, {
      params: { pond_id: selectedPond },
    }),
    enabled: !!farmId && !!selectedPond,
  });

  const cycles = cyclesData?.data || [];

  // Generate report mutation
  const generateReport = useMutation({
    mutationFn: async () => {
      const blob = await api.get<Blob>(
        `/api/v1/farms/${farmId}/reports/cycles/${selectedCycle}/pdf`,
        { responseType: 'blob' }
      );
      downloadFile(
        blob,
        reportFileName(
          ponds.find((p) => p.id === selectedPond)?.name,
          cycles.find((c) => c.id === selectedCycle)?.cycle_number
        )
      );
    },
  });

  const selectedCycleData = cycles.find((c) => c.id === selectedCycle);
  const noCrops = !!selectedPond && !cyclesLoading && !cyclesError && cycles.length === 0;

  return (
    <Card>
      <Card.Body>
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary-100 rounded-lg shrink-0">
            <FileText className="h-6 w-6 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">Cycle Summary Report</h3>
            <p className="text-sm text-gray-500 mt-1">
              Complete cycle report with stocking, water quality trends, feedings, samplings, and harvest data. Suitable for loan applications and compliance documentation.
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          {/* Pond Selection */}
          <Select
            label="Select Pond"
            value={selectedPond}
            onChange={(e) => {
              setSelectedPond(e.target.value);
              setSelectedCycle('');
            }}
            disabled={pondsLoading}
            options={[
              { value: '', label: pondsLoading ? 'Loading ponds...' : 'Choose a pond...' },
              ...ponds.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />

          {/* Cycle Selection */}
          {noCrops && (
            <p className="text-sm text-gray-500">No crops yet for this pond</p>
          )}
          {selectedPond && !noCrops && (
            <Select
              label="Select Cycle"
              value={selectedCycle}
              onChange={(e) => setSelectedCycle(e.target.value)}
              disabled={cyclesLoading}
              options={[
                { value: '', label: cyclesLoading ? 'Loading cycles...' : 'Choose a cycle...' },
                ...cycles.map((c) => ({
                  value: c.id,
                  label: `Cycle ${c.cycle_number || '—'} (${formatDate(c.stocking_date)}) - ${c.status}`,
                })),
              ]}
            />
          )}

          {/* Cycle Preview */}
          {selectedCycleData && (
            <div className="bg-gray-50 rounded-lg p-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-gray-500">Stocked:</span>{' '}
                  <span className="font-medium">{formatDate(selectedCycleData.stocking_date)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>{' '}
                  <span className="font-medium capitalize">{selectedCycleData.status}</span>
                </div>
                <div>
                  <span className="text-gray-500">Cycle #:</span>{' '}
                  <span className="font-medium">{selectedCycleData.cycle_number || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Density:</span>{' '}
                  <span className="font-medium">
                    {selectedCycleData.stocking_density ? `${selectedCycleData.stocking_density}/m²` : '—'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <Button
            onClick={() => generateReport.mutate()}
            disabled={!selectedCycle || generateReport.isPending}
            className="w-full"
          >
            {generateReport.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Generate PDF Report
              </>
            )}
          </Button>

          {generateReport.isError && (
            <p className="text-sm text-status-critical">
              Failed to generate report. Please try again.
            </p>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
